import React from 'react';
import { Box, Grid2, Typography, Paper, Divider,  IconButton, Button } from '@mui/material';
import { PackageDetailsValues } from './PackageConfigHelper';
import styles from '../../Billing/Package/PackageDetails/CounselingDetails/CounselingDetails.module.css';
import { ReactComponent as Edit } from '../../../assets/SVG/Edit.svg';
import { useNavigate } from 'react-router';
import useCurrency from '../../../components/CurrencyProvider';
import TruncatedText from '../../../utils/TruncatedText';
import { useTranslation } from 'react-i18next';

interface Props {
  data: PackageDetailsValues;
  onBack?: () => void;
}

const formatAmount = (v: string | number | null | undefined) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

type Tariff = {
  id?: string | number;
  label?: string;
  name?: string;
  tariff_name?: string;
  code?: string;
  tariff_code?: string;
};

const getTariffName = (tariff: Tariff | null | undefined): string => {
  // Handle different tariff data formats from API
  if (!tariff) return '-';
  return (
    tariff.label ||
    tariff.name ||
    tariff.tariff_name ||
    tariff.code ||
    tariff.tariff_code ||
    '-'
  );
};

// Helper to check if a value is meaningful (not null, undefined, empty, or '-')
const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '' && value !== '-';
  if (typeof value === 'number' || typeof value === 'boolean') return true;
  return false;
};

const NewPackageViewMode: React.FC<Props> = ({ data, onBack }) => {
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  if (!data) return null;

  const getRuleLabel = (ruleCode?: string, fallback?: string) => {
    if (ruleCode === 'CONCESSION') {
      return tBilling('package_config.concession', { defaultValue: 'Concession' });
    }
    if (ruleCode === 'RATE_EDIT') {
      return tBilling('package_config.rate_editable', { defaultValue: 'Rate Editable' });
    }
    if (ruleCode === 'DOCTOR_SHARE') {
      return tBilling('package_config.doctor_share', { defaultValue: 'Doctor Share' });
    }
    return fallback || '-';
  };
  

  const InfoItem: React.FC<{ label?: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box >
      <Typography variant="body2" color="text.secondary" className={styles.label}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 500, mt: 0.5 }} className={styles.label}>{value ?? '-'}</Typography>
    </Box>
  );
  

  return (
    <Box sx={{ p: 3 }} data-testid="new-package-view-mode-container">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6" className={styles.h6Title}>
          {tBilling('package_config.package_details', { defaultValue: 'Package Details' })}
        </Typography>
        {!data.freeze && (
          <IconButton onClick={onBack} sx={{ p: 0 }}>
            <Edit />
          </IconButton>

        )}
      </Box>

      <Paper sx={{ p: 3,borderRadius: 2, boxShadow: 'none' }}>
        <Grid2 container spacing={4}>
          {[ 
            { label: tBilling('package_config.headers.code', { defaultValue: 'Code' }), value: data.code, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            // { label: 'Code Type', value: data.code_type, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            // { label: 'SAC Code', value: data.sac_code, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            { label: tBilling('package_config.headers.package_name', { defaultValue: 'Package Name' }), value: data.package_name, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            { label: tBilling('package_config.headers.effective_date', { defaultValue: 'Effective Date' }), value: data.effective_date, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            { label: tBilling('package_config.headers.expiry_date', { defaultValue: 'Expiry Date' }), value: data.expiry_date, size: { xl: 3, lg: 4, md: 6, xs: 12 } },
            {
              label: tBilling('package_config.headers.package_amount', { defaultValue: 'Package Amount' }),
              value: `${currencySymbol}${formatAmount(data.package_amount || 0)}`,
              size: { xl: 3, lg: 4, md: 6, xs: 12 },
            },
            {
              label: tBilling('package_config.headers.status', { defaultValue: 'Status' }),
              value: data.status
                ? tBilling('package_config.status.active', { defaultValue: 'Active' })
                : tBilling('package_config.status.inactive', { defaultValue: 'Inactive' }),
              size: { xl: 3, lg: 4, md: 6, xs: 12 },
            },
            {
              label: tBilling('package_config.freeze', { defaultValue: 'Freeze' }),
              value: data.freeze ? tBilling('package_config.yes', { defaultValue: 'Yes' }) : null,
              size: { xl: 3, lg: 4, md: 6, xs: 12 },
            },
            {
              label: tBilling('package_config.short_description', { defaultValue: 'Short Description' }),
              value: data.short_description,
              size: { xl: 3, lg: 4, md: 6, xs: 12 },
              truncate: true,
            },
            {
              label: tBilling('package_config.long_description', { defaultValue: 'Long Description' }),
              value: data.long_description,
              size: { xl: 3, lg: 4, md: 6, xs: 12 },
              truncate: true,
            },
          ].filter(f => hasValue(f.value)).map((field) => (
            <Grid2 key={field.label}  size={field.size}>
              <InfoItem 
                label={field.label} 
                value={field.truncate ? <TruncatedText text={field.value as string} title={field.label} iconPosition="left" /> : field.value} 
              />
            </Grid2>
          ))}
        </Grid2>
      </Paper>
      <Divider sx={{ my: 2, borderColor: '#000' }} />

      {data.levels && data.levels.length > 0 && (
        <>
          <Box >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }} className={styles.h6Title}>
              {tBilling('package_config.levels', { defaultValue: 'Levels' })}
            </Typography>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
              <Grid2 container spacing={2}>
                {data.levels.map((lvl, idx) => (
                  <Grid2 key={lvl.id} size={{ xs: 12, md: 3 }}>
                    <InfoItem label={tBilling('package_config.level_with_index', { defaultValue: 'Level {{index}}', index: idx + 1 })} value={lvl.level_name} />
                  </Grid2>
                ))}
              </Grid2>
            </Paper>
          </Box>
          <Divider sx={{ my: 2, borderColor: '#000' }} />
        </>
      
      )}
    
      {/* {data.class_rates && data.class_rates.length > 0 && (
        <Box >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 2 }}
            className={styles.h6Title}
          >
            Class Rates
          </Typography>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
            <Grid2 container spacing={3}>
              {data.class_rates.map((cr) => (
                <Grid2 key={cr.id} size={{ xs: 12, md: 6, lg: 3 }}>
                  <Box sx={{ mb: 2, border: 1, borderColor: 'divider', borderRadius: 3, p: 2  }} >
                    <Box sx={{ mb: 1 }} gap={4} display={'flex'}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.label}
                      >
                      Class :
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }} className={styles.value}>
                        {cr.class_name}
                      </Typography>
                    </Box>
                    <Box gap={4} display={'flex'}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className={styles.label}
                      >
                      Rates :
                      </Typography>
                      <Typography sx={{ fontWeight: 500 }} className={styles.value}>
                        {currencySymbol}{formatAmount(cr.rate)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Paper>
        </Box>
      )} */}
      

      {data.rules && data.rules.length > 0 && (
        <>
          <Box >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }} className={styles.h6Title}>
              {tBilling('package_config.package_rules', { defaultValue: 'Package Rules' })}
            </Typography>
            <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
              <Grid2 container spacing={2}>

                {data.rules.map((rule) => (
                  <Grid2 key={rule.id} size={{ xs: 12, md: 4}}>

                    <Typography variant="body2"  className={styles.value}>
                      {getRuleLabel(rule.rule_code, rule.rule_name)}
                    </Typography>
                    {rule.rule_code === 'CONCESSION' && rule.active && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                        className={styles.label}
                      >
                        {tBilling('package_config.min_percent', { defaultValue: 'Min %' })}: {rule.min_percent ?? '-'} | {tBilling('package_config.max_percent', { defaultValue: 'Max %' })}: {rule.max_percent ?? '-'}
                      </Typography>
                    )}

                    {rule.rule_code === 'DOCTOR_SHARE' && rule.active && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                        className={styles.label}
                      >
                        {tBilling('package_config.percentage_label', { defaultValue: 'Percentage' })}: {rule.percentage ?? '-'}% | {tBilling('package_config.amount', { defaultValue: 'Amount' })}: {currencySymbol}{rule.amount ?? '-'}
                      </Typography>
                    )}

                    {rule.rule_code === 'RATE_EDIT' && rule.active && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                        className={styles.label}
                      >
                        {tBilling('package_config.min_amt', { defaultValue: 'Min Amt' })}: {currencySymbol}{rule.min_amount ?? '-'} | {tBilling('package_config.max_amt', { defaultValue: 'Max Amt' })}: {currencySymbol}{rule.max_amount ?? '-'}
                      </Typography>
                    )}
                  </Grid2>
                ))}
              </Grid2>
            </Paper>
          </Box>        
          <Divider sx={{ my: 2, borderColor: '#000' }} />
        </>
 
        
      )}



      {data.tariffs && data.tariffs.length > 0 && (
        <Box >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }} className={styles.h6Title}>
            {tBilling('package_config.tariff', { defaultValue: 'Tariff' })}
          </Typography>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
            <Grid2 container spacing={2}>
              {data.tariffs.map((lvl) => (
                <Grid2 key={lvl.id} size={{ xs: 12, md: 3 }}>
                  <InfoItem value={getTariffName(lvl)} />
                </Grid2>
              ))}
            </Grid2>
          </Paper>
        </Box>
      )}
      

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <Button variant="outlined" sx={{ minWidth: 160 }} onClick={() => { navigate('/ai/configuration/package-configuration/package', { state: { refetch: true, ts: Date.now() } }); }}>
          {tBilling('package_config.back_to_listing', { defaultValue: 'Back To Listing' })}
        </Button>
      </Box>

      
    </Box>
  );
};

export default NewPackageViewMode;
